import { HttpClient, HttpHeaders, HttpStatusCode } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LabelsControllerGetLabels200Response } from "@ennova-research/waveup-asset-service-sdk";
import { LabelLang, LabelType } from "@ennova-research/waveup-common/enum";
import { Translation } from "@jsverse/transloco";
import {
  BehaviorSubject,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap
} from "rxjs";
import { gatewayBaseUrl } from "../../../shared/utils/gateway/gateway.utils";
import { AppDBService } from "../app-db/app-db.service";
import { DbTranslations } from "../app-db/models/db-translations.model";

@Injectable({
  providedIn: "root"
})
export class I18nService {
  private readonly baseUrl = `${gatewayBaseUrl("/labels")}/labels`;
  private readonly labelsUrl = `${this.baseUrl}/${LabelType.WEB}`;

  isLanguageLoaded = new BehaviorSubject(false);

  constructor(
    private readonly http: HttpClient,
    private readonly appDbService: AppDBService
  ) {}

  getLocaleLabels(language: LabelLang): Observable<Translation> {
    return this.getLabelsFromAppDB(language).pipe(
      switchMap((dbTranslations) => {
        return this.getLocaleLabelsWithEtagCheck(
          language,
          dbTranslations?.etag
        ).pipe(
          map((translations) => translations ?? dbTranslations?.labels ?? {}),
          tap(() => this.isLanguageLoaded.next(true))
        );
      })
    );
  }

  private getLabelsFromAppDB(
    language: LabelLang
  ): Observable<DbTranslations | undefined> {
    const promise = this.appDbService.translations.get(language);

    return from(promise);
  }

  private async saveLabelsToAppDB(
    language: LabelLang,
    etag: string,
    labels: Translation
  ): Promise<void> {
    this.appDbService.translations.put({
      languageCode: language,
      etag,
      labels
    });
  }

  /**
   * @returns the labels translated or null if etag match
   */
  private getLocaleLabelsWithEtagCheck(
    language: LabelLang,
    etag?: string
  ): Observable<Translation | null> {
    if (!etag) {
      return this.getLocaleLabelsFromService(language);
    }

    const headers = new HttpHeaders().set("if-none-match", etag);
    return this.http
      .head(this.labelsUrl, { headers, observe: "response" })
      .pipe(
        switchMap((response) => {
          if (response.status === HttpStatusCode.NoContent) {
            // Etag match. Nothing to retrieve
            return of(null);
          } else {
            return this.getLocaleLabelsFromService(language);
          }
        })
      );
  }

  private getLocaleLabelsFromService(
    language: LabelLang
  ): Observable<Translation> {
    // Retrieves the current locale labels. Current locale is set in headers by i18n interceptor
    return this.http
      .get<LabelsControllerGetLabels200Response>(this.labelsUrl, {
        observe: "response"
      })
      .pipe(
        tap((response) => {
          this.saveLabelsToAppDB(
            language,
            response.headers.get("etag") as string,
            response.body?.data ?? {}
          );
        }),
        map((response) => response.body?.data ?? {})
      );
  }
}
